export const MEAL_PLAN_DOS = [
  "Stay hydrated! Increase water intake up to 3 liters daily.",
  "Use medium portion plates for your meals.",
  "Perform regular exercise or brisk walk (45 mins, 5 days/week).",
  "Prefer low fat foods (baking, grilling, boiling, air frying).",
  "Choose lean sources of protein (egg whites, chicken, fish, legumes).",
  "Prefer complex carbohydrates (oats, barley, quinoa, brown rice).",
  "Use pink Himalayan salt and natural herbs for seasoning.",
  "Maintain a consistent sleep cycle (7–8 hours nightly).",
  "Consume herbal infused water (mint, cinnamon, fennel, cumin).",
  "Prefer sautéed or boiled vegetables for high fiber.",
];

export const MEAL_PLAN_DONTS = [
  "Avoid all sugary beverages (soft drinks, packaged juices).",
  "Do not overload your plate during meal times.",
  "Do not lie down immediately after finishing meals.",
  "Avoid processed/high fat foods (fried items, bakery goods).",
  "Avoid processed meats (sausages, nuggets, deli meats).",
  "Avoid gluten-enriched foods if experiencing bloating.",
  "Avoid processed spices and artificial seasonings/flavors.",
  "Do not consume heavy meals near bedtime (keep 2h gap).",
  "Limit raw vegetables if they cause abdominal bloating.",
  "Avoid excessive gas-inducing foods (beans, chickpeas in excess).",
];

/** Meal sections — options count is dynamic (nutritionist adds/removes via UI). */
export const MEAL_SECTIONS = [
  {
    key: "pre_breakfast",
    optionsKey: "pre_breakfast_options",
    labelKey: "pre_breakfast_label",
    timeKey: "pre_breakfast_time",
    defaultLabel: "Pre-Breakfast",
    defaultTime: "On empty stomach",
    defaultCount: 2,
    note: null,
  },
  {
    key: "breakfast",
    optionsKey: "breakfast_options",
    labelKey: "breakfast_label",
    timeKey: "breakfast_time",
    defaultLabel: "Breakfast",
    defaultTime: "",
    defaultCount: 2,
    note: null,
  },
  {
    key: "snack",
    optionsKey: "snack_options",
    labelKey: "snack_label",
    timeKey: "snack_time",
    defaultLabel: "Mid-day snack",
    defaultTime: "",
    defaultCount: 2,
    note: null,
  },
  {
    key: "lunch",
    optionsKey: "lunch_options",
    labelKey: "lunch_label",
    timeKey: "lunch_time",
    defaultLabel: "Lunch / Mid-day snack",
    defaultTime: "",
    defaultCount: 2,
    note: null,
  },
  {
    key: "dinner",
    optionsKey: "dinner_options",
    labelKey: "dinner_label",
    timeKey: "dinner_time",
    defaultLabel: "Dinner",
    defaultTime: "",
    defaultCount: 2,
    note: null,
  },
];

/** @deprecated use MEAL_SECTIONS */
export const MEAL_OPTION_SECTIONS = MEAL_SECTIONS.map((s) => ({
  key: s.optionsKey,
  label: s.defaultLabel,
  time: `(${s.defaultTime})`,
  count: s.defaultCount,
}));

export const CLOSING_NOTES_DEFAULT =
  "Follow the options alternately and keep the portions as written. Do not eat near bedtime. Keep at least 2 hours between the last meal and sleep.";

export const emptyMealOption = () => ({
  text: "",
  imageUrl: null,
  imageFile: null,
  imagePreview: null,
});

export const emptyMealPlanIntakeForm = () => {
  const base = {
    name: "",
    age_gender: "",
    height: "",
    weight: "",
    bmi: "",
    body_fat_pct: "",
    lifestyle: "",
    assessment_date: "",
    medical_history: "",
    food_allergy: "",
    goals: "",
    recommendations: "",
    carbs_grams: "",
    proteins_grams: "",
    fats_grams: "",
    calories_required: "",
    dos_list: [...MEAL_PLAN_DOS],
    donts_list: [...MEAL_PLAN_DONTS],
    closing_notes: CLOSING_NOTES_DEFAULT,
  };

  MEAL_SECTIONS.forEach((section) => {
    base[section.labelKey] = section.defaultLabel;
    base[section.timeKey] = section.defaultTime;
    base[section.optionsKey] = Array.from({ length: section.defaultCount }, emptyMealOption);
  });

  return base;
};

const normalizeOption = (value) => {
  if (typeof value === "string") {
    return { ...emptyMealOption(), text: value };
  }
  if (value && typeof value === "object") {
    return {
      text: value.text ?? "",
      imageUrl: value.imageUrl ?? value.image ?? null,
      imageFile: value.imageFile ?? null,
      imagePreview: value.imagePreview ?? null,
    };
  }
  return emptyMealOption();
};

const normalizeStringList = (list, fallback) => {
  if (!Array.isArray(list) || list.length === 0) {
    return [...fallback];
  }
  return list.map((item) => String(item ?? "").trim()).filter((item, idx, arr) => {
    return item !== "" || arr.length === 1;
  });
};

export const normalizeMealPlanIntakeForm = (data) => {
  const base = emptyMealPlanIntakeForm();
  if (!data || typeof data !== "object") return base;

  const next = { ...base, ...data };

  next.dos_list = normalizeStringList(data.dos_list, MEAL_PLAN_DOS);
  next.donts_list = normalizeStringList(data.donts_list, MEAL_PLAN_DONTS);
  next.closing_notes =
    data.closing_notes != null && String(data.closing_notes).trim() !== ""
      ? String(data.closing_notes)
      : CLOSING_NOTES_DEFAULT;

  MEAL_SECTIONS.forEach((section) => {
    next[section.labelKey] =
      data[section.labelKey]?.trim?.() || section.defaultLabel;
    next[section.timeKey] =
      data[section.timeKey]?.trim?.() || section.defaultTime;

    const saved = data[section.optionsKey];
    if (Array.isArray(saved) && saved.length > 0) {
      next[section.optionsKey] = saved.map(normalizeOption);
    } else {
      next[section.optionsKey] = Array.from({ length: section.defaultCount }, emptyMealOption);
    }
  });

  return next;
};

export const sanitizeMealPlanIntakeFormForSave = (form) => {
  const data = normalizeMealPlanIntakeForm(form);
  const next = { ...data };

  next.dos_list = data.dos_list.map((t) => t.trim()).filter(Boolean);
  next.donts_list = data.donts_list.map((t) => t.trim()).filter(Boolean);

  MEAL_SECTIONS.forEach(({ optionsKey, labelKey, timeKey }) => {
    next[labelKey] = data[labelKey];
    next[timeKey] = data[timeKey];
    next[optionsKey] = data[optionsKey].map((opt) => ({
      text: opt.text || "",
      imageUrl: opt.imageUrl || null,
    }));
  });

  return next;
};

export const mergeSavedIntakeForm = (savedPayload, currentForm) => {
  const saved = normalizeMealPlanIntakeForm(savedPayload?.form_data || savedPayload);
  const current = normalizeMealPlanIntakeForm(currentForm);
  const next = { ...saved };

  MEAL_SECTIONS.forEach(({ optionsKey }) => {
    const savedLen = saved[optionsKey].length;
    const curLen = current[optionsKey]?.length || 0;
    const maxLen = Math.max(savedLen, curLen);
    next[optionsKey] = Array.from({ length: maxLen }, (_, idx) => ({
      ...normalizeOption(saved[optionsKey][idx]),
      imageFile: current[optionsKey]?.[idx]?.imageFile || null,
      imagePreview: current[optionsKey]?.[idx]?.imagePreview || null,
    }));
  });

  return next;
};

export const mealPlanIntakeFormIsDirty = (current, saved) => {
  if (
    JSON.stringify(sanitizeMealPlanIntakeFormForSave(current)) !==
    JSON.stringify(sanitizeMealPlanIntakeFormForSave(saved))
  ) {
    return true;
  }
  if (collectIntakeFormImageUploads(current).length > 0) return true;
  if (collectIntakeFormImageRemovals(current, saved).length > 0) return true;
  return false;
};

export const collectIntakeFormImageUploads = (form) => {
  const uploads = [];
  MEAL_SECTIONS.forEach(({ optionsKey }) => {
    form[optionsKey].forEach((opt, index) => {
      if (opt.imageFile) {
        uploads.push({ section: optionsKey, index, file: opt.imageFile });
      }
    });
  });
  return uploads;
};

export const collectIntakeFormImageRemovals = (current, saved) => {
  const removals = [];
  MEAL_SECTIONS.forEach(({ optionsKey }) => {
    current[optionsKey].forEach((opt, index) => {
      const prev = saved[optionsKey]?.[index];
      if (prev?.imageUrl && !opt.imageUrl && !opt.imagePreview && !opt.imageFile) {
        removals.push({ section: optionsKey, index });
      }
    });
  });
  return removals;
};

/** Split a list in half for two-column do/don't print layout. */
export const splitListHalves = (list) => {
  const mid = Math.ceil(list.length / 2);
  return [list.slice(0, mid), list.slice(mid)];
};

/** Chunk meal options for print pages (max per page with images). */
export const PRINT_OPTIONS_PER_PAGE = 3;

export const chunkMealOptions = (options, chunkSize = PRINT_OPTIONS_PER_PAGE) => {
  if (!options?.length) {
    return [{ items: [emptyMealOption()], startNumber: 1 }];
  }
  const chunks = [];
  for (let i = 0; i < options.length; i += chunkSize) {
    chunks.push({
      items: options.slice(i, i + chunkSize),
      startNumber: i + 1,
    });
  }
  return chunks;
};

export const formatMealTime = (time) => {
  const t = String(time || "").trim();
  if (!t) return "";
  if (t.startsWith("(")) return t;
  return `(${t})`;
};
